import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { Outlet, useLocation, useNavigate, useOutletContext } from 'react-router-dom';
import contestImage from '../../assets/images/purple-contests-floatin-text.svg';
import ContestNav from '../contests/components/ContestNav';
import { fetchPublicContests, fetchUserContests, updateUserActivity } from '../../utils/api'; // Import API functions
import LoadingContests from '../../assets/images/loading-contests.svg'; // Import loading indicator
import './Contests.css';

const Contests = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [selectedTab, setSelectedTab] = useState('registering');
  const [allContests, setAllContests] = useState([]); // State for all fetched contests
  const [isLoading, setIsLoading] = useState(true); // Loading state for the fetch
  const [error, setError] = useState(null); // Optional: state for fetch errors
  const redirectTimer = useRef(); // Ref to hold the timer ID

  // --- Add useEffect for activity update ---
  useEffect(() => {
    updateUserActivity(); // Fire-and-forget call
  }, []);
  // -----------------------------------------

  // Define loadContests with useCallback
  const loadContests = useCallback(async (tab) => {
    setIsLoading(true);
    setError(null);
    try {
      let contestsData;
      if (tab === 'completed') {
        contestsData = await fetchUserContests();
      } else {
        contestsData = await fetchPublicContests();
      }
      setAllContests(contestsData);
    } catch (err) {
      console.error('Error fetching contests in parent:', err);
      if (err.message === 'Your session has expired. You will be redirected to the login page.') {
        setError('You must be logged in to view completed contests.  Redirecting you to Login page in 5 seconds.'); // Show the error message to the user
        // Redirect after a delay
        redirectTimer.current = setTimeout(() => {
          navigate('/login', { replace: true });
        }, 5000); // 5 seconds delay
      } else {
        setError(err.message || 'Failed to load contests.');
      }
    } finally {
      setIsLoading(false);
    }
  }, [navigate]); // Dependency on navigate is correct

  // Effect to handle tab selection from URL, navigation, and data loading
  useEffect(() => {
    const pathSegments = location.pathname.split('/');
    const tabFromPath = pathSegments[2]; // e.g., 'registering', 'running', or undefined

    if (!tabFromPath) {
      // If URL is generic (e.g., /contests), navigate to default 'registering' tab.
      // loadContests will be triggered by the subsequent run of this effect after navigation.
      navigate('/contests/registering', { replace: true });
    } else {
      // On a specific tab path (e.g., /contests/registering or after the navigation above).
      // This block will also execute after the navigation from the if block above completes.
      setSelectedTab(tabFromPath);
      loadContests(tabFromPath); // Load contests for the current, specific tab.
    }
  }, [location, navigate, loadContests]); // Dependencies

  // Cleanup timer on component unmount
  useEffect(() => {
    return () => {
      clearTimeout(redirectTimer.current);
    };
  }, []);

  // Filter contests based on the selected tab
  const filteredContests = useMemo(() => {
    switch (selectedTab) {
      case 'registering':
        return allContests.filter(c => c.status === 'Registering' || c.status === 'Drafting');
      case 'running':
        return allContests.filter(c => c.status === 'Running');
      case 'completed':
        // Include Graded status as well if applicable
        return allContests.filter(c => c.status === 'Completed' || c.status === 'Graded'); 
      default:
        return [];
    }
  }, [allContests, selectedTab]);

  const handleTabClick = (tab) => {
    setSelectedTab(tab);
    // No need to navigate here, ContestNav likely handles it
  };

  return (
    <div className="contests-page">
      <h1 className="contests-title">
        <img src={contestImage} alt="Contests" /> {/* Updated alt text */}
      </h1>
      <ContestNav selectedTab={selectedTab} onTabClick={handleTabClick} />
      <div className="contests-content">
        {/* Display error message if fetch failed */}
        {error && <div className="error-message">Error: {error}</div>}
        
        {/* Display loading indicator while fetching */}
        {isLoading && !error && (
          <div className="loading-indicator-container"> {/* Optional: Center the loader */}
             <img src={LoadingContests} alt="Loading..." style={{ width: '50px', height: '50px' }} />
          </div>
        )}

        {/* Render Outlet only when not loading and no error, pass context */}
        {!isLoading && !error && (
            <Outlet context={{ contests: filteredContests }} />
        )}
      </div>
    </div>
  );
};

// Custom hook for child components to access the context
export function useContests() {
    return useOutletContext();
}

export default Contests;